import { fmt, fmtCount } from '../utils/formatters';

/**
 * The usual-range strip from the daily brief email (engine/email.py
 * _range_strip), drawn for the web: this shop's last N same-weekdays on a
 * dollar axis — a whisker from low to high, a box for the usual middle half,
 * and a plum ball for yesterday, which may sit outside the whiskers. Same
 * colours as the email; the geometry follows the same padding rule so the
 * two never disagree about where yesterday sits.
 */
export interface UsualRange {
  min: number;
  low: number;
  high: number;
  max: number;
  days: number;
}

const PAD = 0.06; // empty axis each side, as a share of the dollar span
const MIN_BOX_PCT = 2; // a flat usual range (low == high) still draws a visible box

/** Where each piece sits, in percent of the strip's width. */
export function stripGeometry(yesterday: number, usual: UsualRange) {
  const loAxis = Math.min(usual.min, yesterday);
  const hiAxis = Math.max(usual.max, yesterday);
  const span = hiAxis - loAxis || 1;
  const a = loAxis - span * PAD;
  const b = hiAxis + span * PAD;
  const pct = (v: number) => ((v - a) / (b - a)) * 100;
  let boxLeft = pct(usual.low);
  let boxRight = pct(usual.high);
  if (boxRight - boxLeft < MIN_BOX_PCT) {
    const mid = (boxLeft + boxRight) / 2;
    boxLeft = mid - MIN_BOX_PCT / 2;
    boxRight = mid + MIN_BOX_PCT / 2;
  }
  return {
    whiskerLeft: pct(usual.min),
    whiskerRight: pct(usual.max),
    boxLeft,
    boxRight,
    ball: pct(yesterday),
  };
}

// CSS geometry, not a displayed number.
const pc = (v: number) => v + '%';

export default function UsualRangeStrip({ yesterday, usual, weekday, className = '' }: {
  yesterday: number;
  usual: UsualRange;
  weekday: string;
  className?: string;
}) {
  const g = stripGeometry(yesterday, usual);
  const summary =
    `Yesterday ${fmt(yesterday)}, against a usual ${fmt(usual.low)} to ${fmt(usual.high)} ` +
    `over your last ${fmtCount(usual.days)} ${weekday}s; low ${fmt(usual.min)}, high ${fmt(usual.max)}.`;

  return (
    <div className={className}>
      <p className="font-sans text-[11.5px] text-[#6B6670] leading-snug">
        Yesterday <span className="font-semibold text-[#2D2A2E] tabular-nums">{fmt(yesterday)}</span>
        <span className="mx-1.5 text-[#C9C3CE]" aria-hidden="true">·</span>
        vs your last {fmtCount(usual.days)} {weekday}s
      </p>

      <div className="relative h-6 mt-1" role="img" aria-label={summary}>
        {/* the low–high whisker, with a cap at each end */}
        <div className="absolute top-1/2 -translate-y-1/2 h-px bg-[#9C88AE]" style={{ left: pc(g.whiskerLeft), width: pc(g.whiskerRight - g.whiskerLeft) }} />
        <div className="absolute top-1/2 -translate-y-1/2 w-px h-2.5 bg-[#9C88AE]" style={{ left: pc(g.whiskerLeft) }} />
        <div className="absolute top-1/2 -translate-y-1/2 w-px h-2.5 bg-[#9C88AE]" style={{ left: pc(g.whiskerRight) }} />
        {/* the usual box: the middle half */}
        <div className="absolute top-1/2 -translate-y-1/2 h-3 rounded-[3px] bg-[#CFC5DA]" style={{ left: pc(g.boxLeft), width: pc(g.boxRight - g.boxLeft) }} />
        {/* yesterday */}
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#4A2D5E] ring-2 ring-white" style={{ left: pc(g.ball) }} />
      </div>

      <p className="font-sans text-[10.5px] text-[#6B6670] tabular-nums mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        <span>Low <b className="font-medium text-[#2D2A2E]">{fmt(usual.min)}</b></span>
        <span>Usual <b className="font-medium text-[#2D2A2E]">{fmt(usual.low)}–{fmt(usual.high)}</b></span>
        <span>High <b className="font-medium text-[#2D2A2E]">{fmt(usual.max)}</b></span>
      </p>
    </div>
  );
}
